import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { swilRequest } from '../services/swil';
import { indiaDay, outstandingSummary } from '../services/outstanding';
import { createOutstandingSync } from '../services/outstandingSync';

const Context = createContext(null);
export function useCustomerInsights() { return useContext(Context); }

export function CustomerInsightsProvider({ salesman, children }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entries, setEntries] = useState({});
  const [syncProgress, setSyncProgress] = useState(null);
  const [orders, setOrders] = useState({ status: 'loading' });
  const [retry, setRetry] = useState(0);
  const [orderRefresh, setOrderRefresh] = useState(0);
  const [day, setDay] = useState(() => indiaDay());
  const manager = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(null); setCustomers([]); setEntries({}); setSyncProgress(null);
    api.get('get_customers.php', { signal: controller.signal }).then(({ data }) => {
      if (controller.signal.aborted) return;
      const assigned = data.customers || [];
      setCustomers(assigned);
      const sync = createOutstandingSync({
        owner: `salesman:${salesman.id}`, storage: {
          getItem: key => window.localStorage.getItem(key),
          setItem: (key, value) => window.localStorage.setItem(key, value),
        }, onChange: setEntries, onProgress: setSyncProgress,
        fetchRows: async (id, signal) => (await swilRequest('outstanding', { customer_id: id }, { signal })).rows,
      });
      manager.current = sync;
      sync.start(assigned);
    }).catch(failure => { if (!controller.signal.aborted) setError(failure.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    const poll = () => { setDay(indiaDay()); manager.current?.poll(); };
    const interval = setInterval(poll, 60000);
    window.addEventListener('focus', poll);
    return () => { controller.abort(); manager.current?.stop(); manager.current = null; clearInterval(interval); window.removeEventListener('focus', poll); };
  }, [salesman.id, retry]);

  // Read order activity after navigation too, so a newly placed order clears its warning.
  useEffect(() => {
    const controller = new AbortController();
    api.get('customer_order_summary.php', { signal: controller.signal })
      .then(({ data }) => { if (!controller.signal.aborted) setOrders({ ...data, status: 'ready' }); })
      .catch(() => { if (!controller.signal.aborted) setOrders({ status: 'error' }); });
    return () => controller.abort();
  }, [salesman.id, pathname, orderRefresh, day]);

  const balances = useMemo(() => Object.fromEntries(Object.entries(entries).map(([id, entry]) => [id, {
    ...entry, ...(entry.rows ? outstandingSummary(entry.rows) : {}),
    stale: Boolean(entry.updatedAt && indiaDay(new Date(entry.updatedAt)) !== day),
  }])), [entries, day]);
  const value = {
    customers, loading, error, orders, balances, syncProgress,
    retry: () => setRetry(value => value + 1),
    refreshCustomer: id => { manager.current?.refresh(id); setOrderRefresh(value => value + 1); },
    refreshAll: () => { manager.current?.refreshAll(); setOrderRefresh(value => value + 1); },
  };
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function BackgroundSyncStatus() {
  const data = useCustomerInsights();
  const progress = data?.syncProgress;
  const customer = data?.customers.find(customer => String(customer.customer_id) === progress?.currentId);
  return progress?.active ? <div className="background-sync-status" role="status">Loading {Math.min(progress.completed + 1, progress.total)}/{progress.total} · {customer?.customer_name || 'Preparing balances…'}</div> : null;
}

